import React, { useMemo, useState } from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useList } from "@refinedev/core";
import { List, DateField } from "@refinedev/mantine";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Text,
} from "@mantine/core";
import { IconEdit } from "@tabler/icons";
import { useNavigate } from "react-router-dom";
import {
  ReviewEmailTemplate,
  BRAND_OPTIONS,
  BRAND_COLORS,
  BRAND_LABELS,
  EMAIL_TYPE_OPTIONS,
  EMAIL_TYPE_LABELS,
  EMAIL_TYPE_COLORS,
} from "entities/review_email_template";

export const ReviewEmailTemplateList: React.FC<
  IResourceComponentsProps
> = () => {
  const navigate = useNavigate();
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [emailTypeFilter, setEmailTypeFilter] = useState<string | null>(null);

  const { data, isLoading } = useList<ReviewEmailTemplate>({
    resource: "review_email_templates",
    pagination: { pageSize: 100 },
  });

  const templates = data?.data ?? [];

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (brandFilter && t.brand !== brandFilter) return false;
      if (emailTypeFilter && t.email_type !== emailTypeFilter) return false;
      return true;
    });
  }, [templates, brandFilter, emailTypeFilter]);

  const grouped = useMemo(() => {
    const groups: Record<string, ReviewEmailTemplate[]> = {};
    for (const t of filtered) {
      if (!groups[t.brand]) groups[t.brand] = [];
      groups[t.brand].push(t);
    }
    return groups;
  }, [filtered]);

  return (
    <List canCreate={false}>
      <Box sx={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading} />

        <Group spacing="md" mb="lg">
          <Select
            placeholder="Filter by brand"
            data={BRAND_OPTIONS}
            value={brandFilter}
            onChange={setBrandFilter}
            clearable
            sx={{ width: 200 }}
          />
          <Select
            placeholder="Filter by email type"
            data={EMAIL_TYPE_OPTIONS}
            value={emailTypeFilter}
            onChange={setEmailTypeFilter}
            clearable
            sx={{ width: 200 }}
          />
        </Group>

        {Object.entries(grouped).map(([brand, items]) => (
          <Box key={brand} mb="xl">
            <Text weight={700} size="lg" mb="sm">
              {BRAND_LABELS[brand] || brand}
            </Text>
            <Grid gutter="md">
              {items.map((template) => (
                <Grid.Col key={template.id} span={4}>
                  <Paper
                    p="md"
                    radius="md"
                    sx={(theme) => ({
                      border: `1px solid ${
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[4]
                          : theme.colors.gray[2]
                      }`,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    })}
                  >
                    <Group spacing="xs" mb="sm">
                      <Badge
                        color={BRAND_COLORS[template.brand] || "gray"}
                        variant="light"
                        size="sm"
                      >
                        {BRAND_LABELS[template.brand] || template.brand}
                      </Badge>
                      <Badge
                        color={EMAIL_TYPE_COLORS[template.email_type] || "gray"}
                        variant="light"
                        size="sm"
                      >
                        {EMAIL_TYPE_LABELS[template.email_type] ||
                          template.email_type}
                      </Badge>
                    </Group>

                    <Text size="sm" weight={500} lineClamp={2} mb="xs">
                      {template.email_subject || "No subject"}
                    </Text>

                    <Flex mt="auto" justify="space-between" align="center">
                      <Text size="xs" color="dimmed">
                        Updated:{" "}
                        <DateField
                          value={template.updated_at}
                          format="MMM D, YYYY"
                          locales="en"
                        />
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        leftIcon={<IconEdit size={14} />}
                        onClick={() =>
                          navigate(
                            `/review_email_templates/edit/${template.id}`,
                          )
                        }
                      >
                        Edit
                      </Button>
                    </Flex>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>
          </Box>
        ))}

        {!isLoading && filtered.length === 0 && (
          <Text color="dimmed" align="center" py="xl">
            No templates found matching the current filters.
          </Text>
        )}
      </Box>
    </List>
  );
};
